import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, School, FileText, Calendar, Inbox, Search as SearchIcon, Users, Settings,
  Home, LogOut, BookOpen, ClipboardList, ClipboardCheck, Wallet, MessageSquare, CalendarDays,
  GraduationCap, Award, Library, Bus, Bell, CalendarClock, ChevronRight, ChevronDown, IdCard,
  Truck, Receipt, ShieldCheck, Boxes, BarChart3, UserCog, Menu, X,
} from "lucide-react";

const link = (to, label, icon, opts = {}) => ({ to, label, icon, ...opts });
const grp = (label, icon, children) => ({ label, icon, children });

const superLinks = [
  link("/super", "Dashboard", LayoutDashboard, { end: true }),
  link("/super/schools", "Schools", School),
  link("/super/blogs", "Blogs", FileText),
  link("/super/events", "Events", Calendar),
  link("/super/submissions", "Submissions", Inbox),
  link("/super/enquiries", "Parent Enquiries", MessageSquare),
  link("/super/seo", "SEO & URLs", SearchIcon),
  link("/super/settings", "Site Settings", Settings),
  link("/super/pages", "Pages", FileText),
  link("/super/users", "Users", Users),
];

const schoolLinks = [
  link("/school", "Dashboard", LayoutDashboard, { end: true }),
  grp("Admissions", ClipboardList, [
    link("/school/enquiries", "Enquiries", Inbox),
    link("/school/admissions", "Applications", ClipboardList),
    link("/erp/referrals", "Referrals", UserCog),
  ]),
  grp("Students", Users, [
    link("/school/students", "All Students", Users),
    link("/erp/documents", "Documents", FileText),
  ]),
  grp("Attendance", ClipboardCheck, [
    link("/school/attendance", "Daily Attendance", ClipboardCheck),
    link("/erp/session-attendance", "Session Attendance", CalendarDays),
    link("/erp/reports?tab=attendance", "Attendance Reports", BarChart3),
  ]),
  grp("Academics", BookOpen, [
    link("/erp/sessions", "Academic Sessions", CalendarClock),
    link("/erp/subjects", "Subjects", BookOpen),
    link("/school/timetable", "Timetable", CalendarDays),
    link("/erp/homework", "Homework", ClipboardList),
  ]),
  grp("Examination", Award, [
    link("/school/exams", "Exams & Results", BookOpen),
    link("/erp/marks", "Marks Entry", ClipboardList),
    link("/erp/admit-cards", "Admit Cards", IdCard),
    link("/erp/report-cards", "Report Cards", Award),
    link("/erp/certificates", "Certificates", ShieldCheck),
  ]),
  grp("Fees", Wallet, [
    link("/school/fees", "Fees ERP", Wallet),
    link("/erp/reports?tab=fees", "Fee Reports", Receipt),
  ]),
  grp("Transport", Truck, [
    link("/erp/transport", "Buses / Drivers / Routes", Truck),
    link("/erp/pickup", "Pickup / Drop", Bus),
  ]),
  grp("Library & Assets", Library, [
    link("/erp/library", "Library", Library),
    link("/erp/assets", "Assets", Boxes),
  ]),
  grp("Documents", FileText, [
    link("/erp/id-cards", "ID Cards", IdCard),
    link("/erp/certificates", "Certificates", ShieldCheck),
    link("/erp/tc", "Transfer Certificates", FileText),
  ]),
  grp("Communication", MessageSquare, [
    link("/erp/notices", "Notices", Bell),
    link("/erp/communications", "Send Message", MessageSquare),
    link("/school/messaging", "WhatsApp", MessageSquare),
  ]),
  grp("HR", Users, [
    link("/school/staff", "Staff & Transport", Users),
    link("/school/leaves", "Leaves", ClipboardCheck),
    link("/erp/payroll", "Payroll", Wallet),
  ]),
  link("/erp/reports", "Reports Centre", BarChart3),
  grp("Settings", Settings, [
    link("/school/profile", "School Profile", School),
    link("/erp/roles", "Roles & Permissions", UserCog),
    link("/erp/audit", "Audit Logs", ShieldCheck),
  ]),
];

const teacherLinks = [
  link("/teacher", "Dashboard", LayoutDashboard, { end: true }),
  link("/school/students", "Students", Users),
  grp("Attendance", ClipboardCheck, [
    link("/school/attendance", "Daily", ClipboardCheck),
    link("/erp/session-attendance", "Session-wise", CalendarDays),
  ]),
  link("/school/timetable", "My Timetable", CalendarDays),
  link("/erp/homework", "Homework", ClipboardList),
  link("/school/exams", "Exams", BookOpen),
  link("/erp/marks", "Marks Entry", ClipboardList),
  link("/erp/notices", "Notices", Bell),
  link("/school/leaves", "Leaves & Timesheet", CalendarDays),
];

const studentLinks = [
  link("/student", "Dashboard", LayoutDashboard, { end: true }),
  { hash: "st-attendance", label: "Attendance", icon: ClipboardCheck },
  { hash: "st-timetable", label: "Timetable", icon: Calendar },
  link("/erp/homework", "Homework", ClipboardList),
  { hash: "st-exams", label: "Exams", icon: BookOpen },
  { hash: "st-results", label: "Results", icon: Award },
  { hash: "st-fees", label: "Fees", icon: Wallet },
  { hash: "st-library", label: "Library", icon: Library },
  { hash: "st-bus", label: "Bus", icon: Bus },
  { hash: "st-documents", label: "Documents", icon: FileText },
  link("/erp/notices", "Notices", Bell),
];

const parentLinks = [
  link("/parent", "Dashboard", LayoutDashboard, { end: true }),
  { hash: "pp-attendance", label: "Attendance", icon: ClipboardCheck },
  { hash: "pp-fees", label: "Fee Status", icon: Wallet },
  { hash: "pp-exams", label: "Exam Schedule", icon: BookOpen },
  { hash: "pp-results", label: "Results", icon: Award },
  { hash: "pp-homework", label: "Homework", icon: ClipboardList },
  { hash: "pp-bus", label: "Bus Tracking", icon: Bus },
  { hash: "pp-notices", label: "Notices", icon: Bell },
  { hash: "pp-documents", label: "Documents", icon: FileText },
  { hash: "pp-leave", label: "Apply Leave", icon: CalendarClock },
];

const VARIANTS = {
  super: { links: superLinks, label: "Super Admin", accent: "bg-slate-900" },
  school: { links: schoolLinks, label: "School Admin", accent: "bg-blue-700" },
  teacher: { links: teacherLinks, label: "Teacher", accent: "bg-blue-700" },
  student: { links: studentLinks, label: "Student", accent: "bg-emerald-700" },
  parent: { links: parentLinks, label: "Parent Portal", accent: "bg-amber-600" },
};

// Mirror of App.js ProtectedRoute roles — menu buttons whose route excludes the
// current role are never rendered (no dead/forbidden buttons in the sidebar).
const ROUTE_ROLES = {
  "/teacher": ["teacher", "school_admin"],
  "/student": ["student", "parent"],
  "/parent": ["parent"],
  "/super/enquiries": ["super_admin"],
  "/school": ["school_admin", "teacher"],
  "/school/admissions": ["school_admin", "teacher"],
  "/school/enquiries": ["school_admin", "super_admin"],
  "/school/students": ["school_admin", "teacher"],
  "/school/attendance": ["school_admin", "teacher"],
  "/school/exams": ["school_admin", "teacher"],
  "/school/fees": ["school_admin"],
  "/school/messaging": ["school_admin"],
  "/school/timetable": ["school_admin", "teacher"],
  "/school/leaves": ["school_admin", "teacher", "parent"],
  "/school/staff": ["school_admin"],
  "/school/profile": ["school_admin"],
  "/erp/sessions": ["school_admin", "super_admin", "teacher"],
  "/erp/subjects": ["school_admin", "super_admin", "teacher"],
  "/erp/homework": ["school_admin", "super_admin", "teacher", "parent", "student"],
  "/erp/notices": ["school_admin", "super_admin", "teacher", "parent", "student"],
  "/erp/communications": ["school_admin", "super_admin"],
  "/erp/audit": ["school_admin", "super_admin"],
  "/erp/session-attendance": ["school_admin", "super_admin", "teacher", "parent", "student"],
  "/erp/transport": ["school_admin", "super_admin"],
  "/erp/pickup": ["school_admin", "super_admin", "teacher"],
  "/erp/referrals": ["school_admin", "super_admin"],
  "/erp/documents": ["school_admin", "super_admin", "teacher"],
  "/erp/marks": ["school_admin", "super_admin", "teacher"],
  "/erp/admit-cards": ["school_admin", "super_admin"],
  "/erp/report-cards": ["school_admin", "super_admin"],
  "/erp/certificates": ["school_admin", "super_admin"],
  "/erp/id-cards": ["school_admin", "super_admin"],
  "/erp/tc": ["school_admin", "super_admin"],
  "/erp/library": ["school_admin", "super_admin", "teacher"],
  "/erp/assets": ["school_admin", "super_admin"],
  "/erp/payroll": ["school_admin", "super_admin"],
  "/erp/reports": ["school_admin", "super_admin"],
  "/erp/roles": ["school_admin", "super_admin"],
};

const permitted = (to, role) => {
  if (!to) return true; // hash scroll links
  const allowed = ROUTE_ROLES[to.split("?")[0]];
  return !allowed || allowed.includes(role);
};

const filterLinks = (links, role) =>
  links
    .map((l) => (l.children ? { ...l, children: l.children.filter((c) => permitted(c.to, role)) } : l))
    .filter((l) => (l.children ? l.children.length > 0 : permitted(l.to, role)));

// Fixed bottom quick-nav shown only on <lg for teacher/parent portals.
// "action" items trigger the page's linkActions (e.g. switch to the Fees tab) when available.
const BOTTOM_TABS = {
  teacher: [
    { to: "/teacher", label: "Home", icon: LayoutDashboard },
    { to: "/school/attendance", label: "Attendance", icon: ClipboardCheck },
    { to: "/erp/homework", label: "Homework", icon: ClipboardList },
    { to: "/erp/notices", label: "Notices", icon: Bell },
  ],
  parent: [
    { to: "/parent", label: "Home", icon: LayoutDashboard },
    { action: "Fee Status", fallbackTo: "/parent", label: "Fees", icon: Wallet },
    { action: "Homework", fallbackTo: "/parent", label: "Homework", icon: ClipboardList },
    { action: "Notices", fallbackTo: "/parent", label: "Notices", icon: Bell },
  ],
};

export const scrollToId = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const testId = (label) => `sidebar-${label.toLowerCase().replace(/\s+&?\s?/g, "-")}`;

function Item({ l, linkActions }) {
  if (l.hash) {
    return (
      <button
        type="button"
        data-testid={testId(l.label)}
        onClick={() => (linkActions?.[l.label] ? linkActions[l.label]() : scrollToId(l.hash))}
        className="w-full flex items-center gap-3 px-6 py-2 text-sm text-left text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
      >
        <l.icon className="w-4 h-4" />
        {l.label}
      </button>
    );
  }
  return (
    <NavLink
      to={l.to}
      end={l.end}
      data-testid={testId(l.label)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-6 py-2 text-sm transition-colors ${
          isActive
            ? "bg-slate-100 text-slate-900 font-semibold border-r-2 border-slate-900"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        }`
      }
    >
      <l.icon className="w-4 h-4" />
      {l.label}
    </NavLink>
  );
}

function Group({ g, linkActions }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        data-testid={testId(g.label + "-group")}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-6 py-2 text-sm text-slate-700 font-semibold hover:bg-slate-50"
      >
        <g.icon className="w-4 h-4" />
        <span className="flex-1 text-left">{g.label}</span>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="bg-slate-50/60 border-l-2 border-slate-200 ml-6">
          {g.children.map((c) => <Item key={c.to || c.hash || c.label} l={c} linkActions={linkActions} />)}
        </div>
      )}
    </div>
  );
}

export default function DashLayout({ variant = "super", title, children, linkActions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const v = VARIANTS[variant] || VARIANTS.super;
  const links = filterLinks(v.links, user?.role || "");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer whenever the route changes so nav doesn't leave the overlay open.
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  // Lock body scroll when drawer is open on mobile.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      {/* -------- Mobile top bar (hamburger) -------- */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 h-14 flex items-center px-3 gap-3">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-1 rounded-md hover:bg-slate-100 active:bg-slate-200"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6 text-slate-700" />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-7 h-7 rounded-md ${v.accent} flex items-center justify-center shrink-0`}>
            <GraduationCap className="w-4 h-4 text-amber-400" />
          </div>
          <div className="min-w-0">
            <div className="font-black tracking-tight text-sm leading-tight">School Dial</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold leading-tight truncate">{title || v.label}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={async () => { await logout(); nav("/"); }}
          className="ml-auto p-2 -mr-1 rounded-md hover:bg-slate-100 active:bg-slate-200 text-slate-600"
          data-testid="mobile-logout-btn"
          aria-label="Logout"
          title="Logout"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* -------- Mobile backdrop -------- */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-40 animate-in fade-in"
          data-testid="mobile-drawer-backdrop"
        />
      )}

      {/* -------- Sidebar (off-canvas drawer < lg, sticky ≥ lg) -------- */}
      <aside
        data-testid={`${variant}-sidebar`}
        className={`w-64 bg-white border-r border-slate-200 flex flex-col shrink-0 fixed lg:sticky top-0 left-0 h-screen z-50 transform transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="p-6 border-b border-slate-200 flex items-start gap-2">
          <div className={`w-8 h-8 rounded-md ${v.accent} flex items-center justify-center`}>
            <GraduationCap className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-black tracking-tight">School Dial</div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mt-1">
              {v.label}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1 -mr-1 -mt-1 rounded-md hover:bg-slate-100"
            data-testid="mobile-drawer-close"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {links.map((item, idx) =>
            item.children ? (
              <Group key={item.label + idx} g={item} linkActions={linkActions} />
            ) : (
              <Item key={item.to || item.hash || item.label} l={item} linkActions={linkActions} />
            )
          )}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 mb-2 truncate">{user?.email}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="flex-1" onClick={() => nav("/")} data-testid="sidebar-home-btn">
              <Home className="w-3.5 h-3.5 mr-1" /> Site
            </Button>
            <Button size="sm" variant="outline" className="flex-1" onClick={async () => { await logout(); nav("/"); }} data-testid="sidebar-logout-btn">
              <LogOut className="w-3.5 h-3.5 mr-1" /> Exit
            </Button>
          </div>
        </div>
      </aside>

      {/* -------- Main content -------- */}
      <main className="flex-1 min-w-0 overflow-x-hidden pt-14 lg:pt-0">
        {title && (
          <div className="hidden lg:block border-b border-slate-200 bg-white px-8 py-5">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="dash-title">{title}</h1>
          </div>
        )}
        <div className={`p-4 sm:p-6 lg:p-8 ${BOTTOM_TABS[variant] ? "pb-24 lg:pb-8" : ""}`}>{children}</div>
      </main>

      {/* -------- Mobile bottom quick-nav (teacher / parent portals) -------- */}
      {BOTTOM_TABS[variant] && (
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
          data-testid="mobile-bottom-nav"
        >
          {BOTTOM_TABS[variant].map((t) => {
            const Icon = t.icon;
            const active = t.to ? loc.pathname === t.to : false;
            const onClick = () => {
              if (t.action && linkActions?.[t.action]) linkActions[t.action]();
              else if (t.to) nav(t.to);
              else if (t.fallbackTo) nav(t.fallbackTo);
            };
            return (
              <button
                key={t.label}
                type="button"
                onClick={onClick}
                data-testid={`bottomnav-${t.label.toLowerCase()}`}
                className={`flex-1 flex flex-col items-center gap-0.5 py-2 min-h-[52px] text-[10px] font-semibold transition-colors ${
                  active ? "text-blue-700" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon className="w-5 h-5" />
                {t.label}
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
